import { Link } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import { faArrowRightArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import {
    faBadgeCheck,
    faBuildingColumns,
    faClock,
    faMobile,
    faReceipt,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Dialog from 'components/dialog';
import { AnchorHashIds } from 'constants/anchor-hash-ids';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import CircleCheck from 'images/recommended-actions/circle-check.yellow.svg';

interface RecommendedActionsVerifyNonProfitDialogProps extends Dialog.DialogProps {}
const RecommendedActionsVerifyNonProfitDialog = ({
    children,
    ...rest
}: RecommendedActionsVerifyNonProfitDialogProps) => {
    const { accountId } = useCurrentAccount();
    const listItemClass = 'flex items-center gap-2 text-sm text-gray-700 text-nowrap';
    const iconClass = 'w-4 shrink-0 text-gray-400';
    return (
        <Dialog.Root {...rest}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content className="max-w-[400px] gap-4 p-4 sm:gap-6 sm:p-6">
                    <div className="bg-brand-butter-light flex h-36 w-full items-center justify-center rounded-xl">
                        <img src={CircleCheck} alt="" className="size-11" />
                    </div>
                    <div>
                        <Dialog.Title className="text-center text-base sm:text-lg">
                            Let’s verify your nonprofit first
                        </Dialog.Title>
                        <p className="text-center text-sm text-gray-600">
                            Verify your nonprofit to send emails & texts to your supporters and
                            unlock more benefits!
                        </p>
                    </div>
                    <ul className="flex flex-col gap-2 sm:px-3">
                        <li className={listItemClass}>
                            <FontAwesomeIcon icon={faClock} className={iconClass} />
                            Funds raised available faster (48 hours)
                        </li>
                        <li className={listItemClass}>
                            <FontAwesomeIcon icon={faBadgeCheck} className={iconClass} />
                            Verification badge on your campaign pages
                        </li>
                        <li className={listItemClass}>
                            <FontAwesomeIcon icon={faReceipt} className={iconClass} />
                            EIN number displayed on your email receipts
                        </li>
                        <li className={listItemClass}>
                            <FontAwesomeIcon icon={faBuildingColumns} className={iconClass} />
                            Accept ACH & DAF payments
                        </li>
                        <li className={listItemClass}>
                            <FontAwesomeIcon icon={faMobile} className={iconClass} />
                            <span>
                                Outbound text blasts (requires{' '}
                                <Link to={`/accounts/${accountId}/settings/about/subscription`}>
                                    Plus
                                </Link>
                                )
                            </span>
                        </li>
                        <li className={listItemClass}>
                            <FontAwesomeIcon icon={faArrowRightArrowLeft} className={iconClass} />
                            Automated payouts
                        </li>
                    </ul>
                    <div className="mt-2 flex flex-col-reverse gap-3 sm:flex-row">
                        <Dialog.Cancel asChild>
                            <Button variant="outline" color="neutral" className="w-full">
                                Maybe later
                            </Button>
                        </Dialog.Cancel>
                        <Link
                            to={`/accounts/${accountId}/settings/about/verify#${AnchorHashIds.VerifyNonprofit}`}
                            className="w-full"
                        >
                            <Button variant="solid" className="w-full">
                                Let's go!
                            </Button>
                        </Link>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const RecommendedActionsVerifyNonProfitDialogTrigger = Dialog.Trigger;

const Root = RecommendedActionsVerifyNonProfitDialog;
const Trigger = RecommendedActionsVerifyNonProfitDialogTrigger;

export {
    RecommendedActionsVerifyNonProfitDialog,
    RecommendedActionsVerifyNonProfitDialogTrigger,
    //
    Root,
    Trigger,
};
